import { and, eq, isNull } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@vigia/powersync";

/**
 * ── Local-first writes that CANNOT be lost silently (§4.11) ─────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can match
 * **ZERO rows and return «success»**. This is not a hypothesis: it is measured in
 * `space-academy-app` (same canon, local-stack `activity` table, course
 * `06a131d0`) — the course is created, 1.03 s later the unit is created and **in
 * between NO `course/updated` op exists**: the title the author had just
 * written never generated a CRUD operation.
 *
 * The mechanism: when the CRUD queue drains, PowerSync **rebuilds the
 * local view from the last checkpoint**. If the checkpoint does not yet carry the
 * newly created row, the row is **ABSENT during that instant** — and an
 * UPDATE against it writes nothing, generates no op and **does not fail**. The window is not
 * «X ms passed»: it is «the queue drained in the middle», and that cannot be detected
 * after the fact.
 *
 * Why the absence of an error proves nothing here: the server write-path
 * returns **2xx always on purpose** (a 4xx would block the client's queue
 * forever). That case is a DIFFERENT path —there the server rejects and
 * stays silent—, but the criterion is the same: a user write that
 * disappears without a trace is the worst failure mode. In fleet management, this
 * window is the operator resolving an alert, deciding a remote-control
 * consent or archiving a device right after creating/enrolling it.
 *
 * Contract of this module: it writes, it **RE-READS**, it compares. If the value did
 * not land it retries with backoff; if after exhausting it still has not landed, it
 * **THROWS** — the caller communicates it instead of leaving the operator looking at a
 * field that represents nothing stored.
 *
 * Unlike the `listto-app` canon (`9ae347b6`), `patchRowVerified` does NOT
 * inject `updated_at` into the patch: in Vigía's schema neither `device_alert` nor
 * `remote_control_session` has that column (only `device` and `user`
 * have it), so stamping the timestamp in here would break those tables at
 * RUNTIME (the PowerSync view is real SQL, not a lax object). Whoever
 * wants to touch `updated_at` includes it explicitly in their own `patch`.
 *
 * ── What this module does NOT cover (deliberate) ────────────────────────────
 * Hard `DELETE`s are not verified: their natural check would be «the row is no
 * longer there», and «absent» is precisely the ambiguous state of this window — you
 * cannot distinguish «it was deleted» from «it had not arrived yet».
 *
 * ── `scope`: tenant isolation, not just correctness (§4.14) ─────────────────
 * An `UPDATE … WHERE id = ?` is correct insofar as `id` is a unique PK —
 * but `infra/sync_streams.yaml` syncs `device`/`device_alert`/
 * `remote_control_session` with `WHERE organization_id IN my_org_ids`, where
 * `my_org_ids` are ALL the organizations the user is a
 * `member` of — not just the one being viewed at that moment. An operator who
 * belongs to two fleets has BOTH synced at once in SQLite. Without an additional
 * filter, an `UPDATE` issued believing it acts on
 * organization A lands equally on a row of organization B if the
 * `id` (tampered, desynced, or simply wrong) points there.
 *
 * `options.scope` adds those conditions — typically `organization_id` —
 * applied BOTH to the UPDATE and to the re-read: a row that does not satisfy them
 * is treated the same as an absent row, and `patchRowVerified` ends up throwing
 * `LocalWriteLostError` instead of faking success.
 *
 * Deliberate divergence from the `comandero-app` canon (`f8f1049`): there
 * `ScopeCondition` only uses `eq()` because `organization_id` is never NULL in
 * its POS (every order belongs to a venue). Here `device.organization_id`
 * **CAN** be NULL —personal mode, see the YAML header: "Personal
 * data: organization_id IS NULL → only the owner sees them"— and `eq(col,
 * null)` compiles to `col = NULL`, which in real SQL is NEVER true (not even for
 * genuinely NULL rows). Copying the canon verbatim would have broken all of
 * personal mode (`LocalWriteLostError` permanent). That is why a scope condition
 * with a null `value` translates to `isNull(column)`, not to `eq(column, null)`.
 */

/**
 * Retry backoff. Total ≈ 6.05 s: more than enough to cover the round trip of
 * a checkpoint (measured at ~1 s against the local stack) without leaving a
 * quick interaction —resolving an alert, deciding a consent— hanging
 * forever if the row genuinely does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Additional equality condition — tenant isolation in depth (§4.14). */
export interface ScopeCondition {
  column: SQLiteColumn;
  value: unknown;
}

export interface VerifiedWriteOptions {
  /** Waits between retries. Exposed so tests do not last seconds. */
  backoffMs?: readonly number[];
  /**
   * Extra conditions the row must ALSO satisfy, besides `id` —
   * typically `organization_id`. They are applied both to the UPDATE and to the
   * re-read: a row that does not satisfy the scope is treated the same as an
   * absent row (see the security note §4.14 above). `value: null` translates to
   * `IS NULL`, not to `= NULL` (see why in the same note).
   */
  scope?: readonly ScopeCondition[];
}

/** `eq()` for real values, `isNull()` for `null`/`undefined` — never `= NULL`. */
function scopeConditionSQL(condition: ScopeCondition) {
  return condition.value === null || condition.value === undefined
    ? isNull(condition.column)
    : eq(condition.column, condition.value);
}

/** Builds `eq(id) AND scope1 AND …` — or just `eq(id)` without scope. */
function idAndScopeCondition(idColumn: SQLiteColumn, id: string, scope: readonly ScopeCondition[]) {
  if (scope.length === 0) {
    return eq(idColumn, id);
  }
  return and(eq(idColumn, id), ...scope.map(scopeConditionSQL));
}

/** Error of a local-first write that never landed in the view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `The local write did not land in row ${rowId} (fields: ${fields.join(", ")}). ` +
        `The row is not in the PowerSync view or the value was not saved.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/**
 * Reads the row by id (+ `scope`, if given), or `undefined` if it is not in the
 * local view or does not satisfy the scope — same result as "absent row", on
 * purpose (§4.14).
 */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  scope: readonly ScopeCondition[] = [],
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read
  // is by primary key (+ scope), so the row is a flat record of
  // columns.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const rows = (await db
    .select()
    .from(table)
    .where(idAndScopeCondition(idColumn, id, scope))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** `null` and `undefined` are the same «empty» when comparing against SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * **Verified** `UPDATE … WHERE id = ?`: writes, re-reads, compares the patch
 * fields and retries while they have not landed. Throws `LocalWriteLostError`
 * if after exhausting the backoff they are still not there.
 *
 * The `patch` is written AS IS — this module neither adds nor removes fields (see
 * the header note about `updated_at`).
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set(patch)
      .where(idAndScopeCondition(idColumn, id, scope));

    const row = await readRow(db, table, idColumn, id, scope);
    if (row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value))) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(patch));
    }
    await sleep(wait);
  }
}

/**
 * Waits for the row to EXIST in the local view before touching it.
 *
 * For mutations that are not a simple patch, or that need to distinguish
 * «the row is hidden by the window» from «the row is genuinely new» before
 * deciding whether to INSERT or UPDATE (see `enrollDeviceMutation` in
 * `fleet/mutations.ts`, §4.11 lesson 1: deciding with a loose SELECT can
 * lead to re-inserting over a row that was only hidden, losing
 * `archived`/`enrolled_at`).
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<missing row>"]);
    }
    await sleep(wait);
  }
}
